import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { toast } from 'sonner';
import { Download, Upload, FileSpreadsheet, Users, Wrench, Package, MapPin, AlertCircle, CheckCircle, FileDown,
         Building2, Ticket, ArrowLeftRight, Hammer, BarChart3, BoxSelect, Stethoscope, Search, ClipboardList, ShoppingCart, Layers } from 'lucide-react';

const ImportExportPage = () => {
  const { hasRole } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);

  const handleExport = async (type) => {
    try {
      const response = await api.get(`/export/${type}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${type}_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`Export ${type} finalizat!`);
    } catch (error) {
      toast.error(`Eroare la export: ${error.message}`);
    }
  };

  const handleDownloadTemplate = async (type) => {
    try {
      const response = await api.get(`/templates/${type}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `template_${type}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Template descarcat!');
    } catch (error) {
      toast.error('Eroare la descarcare template');
    }
  };

  const handleImport = async (type) => {
    if (!selectedFile) {
      toast.error('Selecteaza un fisier Excel');
      return;
    }

    try {
      setUploading(true);
      setImportResult(null);
      
      const formData = new FormData();
      formData.append('file', selectedFile);
      
      const response = await api.post(`/import/${type}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setImportResult({
        success: true,
        message: response.data.message,
        imported: response.data.imported,
        errors: response.data.errors
      });
      
      toast.success(response.data.message);
      setSelectedFile(null);
    } catch (error) {
      setImportResult({
        success: false,
        message: error.response?.data?.detail || 'Eroare la import',
        errors: []
      });
      toast.error('Eroare la import');
    } finally {
      setUploading(false);
    }
  };

  const exportItems = [
    { type: 'equipment', label: 'Echipamente', icon: Wrench, adminOnly: false },
    { type: 'consumables', label: 'Consumabile / Articole', icon: Package, adminOnly: false },
    { type: 'consumable-categories', label: 'Categorii Consumabile', icon: Layers, adminOnly: false },
    { type: 'stock_current', label: 'Stoc Curent', icon: BarChart3, adminOnly: false },
    { type: 'stock_movements', label: 'Miscari Stoc', icon: ArrowLeftRight, adminOnly: false },
    { type: 'tickets', label: 'Tichete / Probleme', icon: Ticket, adminOnly: false },
    { type: 'revisions', label: 'Revizii / Interventii', icon: FileSpreadsheet, adminOnly: false },
    { type: 'tools', label: 'Scule / Unelte', icon: Hammer, adminOnly: false },
    { type: 'material_requests', label: 'Necesar Consumabile', icon: ShoppingCart, adminOnly: false },
    { type: 'users', label: 'Utilizatori', icon: Users, adminOnly: true },
    { type: 'plants', label: 'Sedii', icon: Building2, adminOnly: true },
    { type: 'locations', label: 'Locatii', icon: MapPin, adminOnly: false },
    { type: 'suppliers', label: 'Furnizori', icon: Package, adminOnly: false },
    { type: 'bins', label: 'BIN-uri (Locatii depozitare)', icon: BoxSelect, adminOnly: false },
    { type: 'symptoms', label: 'Simptome Interventii', icon: Stethoscope, adminOnly: false },
    { type: 'causes', label: 'Cauze Interventii', icon: Search, adminOnly: false },
    { type: 'intervention_types', label: 'Tipuri Interventii', icon: ClipboardList, adminOnly: false }
  ];

  const importItems = [
    { type: 'equipment', label: 'Echipamente', icon: Wrench, adminOnly: false },
    { type: 'consumables', label: 'Consumabile / Articole', icon: Package, adminOnly: false },
    { type: 'consumable-categories', label: 'Categorii Consumabile', icon: Layers, adminOnly: false },
    { type: 'users', label: 'Utilizatori', icon: Users, adminOnly: true },
    { type: 'plants', label: 'Sedii', icon: Building2, adminOnly: true },
    { type: 'locations', label: 'Locatii', icon: MapPin, adminOnly: true },
    { type: 'suppliers', label: 'Furnizori', icon: Package, adminOnly: false },
    { type: 'tools', label: 'Scule / Unelte', icon: Hammer, adminOnly: false },
    { type: 'symptoms', label: 'Simptome Interventii', icon: Stethoscope, adminOnly: false },
    { type: 'causes', label: 'Cauze Interventii', icon: Search, adminOnly: false },
    { type: 'intervention_types', label: 'Tipuri Interventii', icon: ClipboardList, adminOnly: false }
  ];

  return (
    <div className="space-y-6" data-testid="import-export-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Import / Export</h1>
        <p className="text-slate-600 mt-1">Importa si exporta date in format Excel</p>
      </div>

      <Tabs defaultValue="export" className="w-full">
        <TabsList className="grid w-full grid-cols-2 h-12">
          <TabsTrigger value="export" className="flex items-center gap-2">
            <Download className="w-4 h-4" />
            Export
          </TabsTrigger>
          <TabsTrigger value="import" className="flex items-center gap-2">
            <Upload className="w-4 h-4" />
            Import
          </TabsTrigger>
        </TabsList>

        {/* Export Tab */}
        <TabsContent value="export" className="space-y-4">
          <Card className="card-industrial">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Download className="w-5 h-5" />
                Exporta Date
              </CardTitle>
              <CardDescription>Descarca datele in format Excel (.xlsx)</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {exportItems.map((item) => {
                  const Icon = item.icon;
                  if (item.adminOnly && !hasRole('admin')) return null;
                  return (
                    <Card key={item.type} className="border hover:shadow-md transition-shadow">
                      <CardContent className="p-4">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                              <Icon className="w-5 h-5 text-[#0052CC]" />
                            </div>
                            <div>
                              <p className="font-medium">{item.label}</p>
                              <p className="text-xs text-slate-500">Export Excel</p>
                            </div>
                          </div>
                          <Button 
                            size="sm" 
                            onClick={() => handleExport(item.type)}
                            data-testid={`export-${item.type}-btn`}
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Import Tab */}
        <TabsContent value="import" className="space-y-4">
          <Card className="card-industrial">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Upload className="w-5 h-5" />
                Importa Date
              </CardTitle>
              <CardDescription>Incarca date din fisiere Excel (.xlsx)</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Template Downloads */}
              <div>
                <Label className="text-sm font-medium mb-2 block">1. Descarca Template</Label>
                <div className="flex flex-wrap gap-2">
                  {importItems.map((item) => {
                    if (item.adminOnly && !hasRole('admin')) return null;
                    return (
                      <Button
                        key={item.type}
                        variant="outline"
                        size="sm"
                        onClick={() => handleDownloadTemplate(item.type)}
                      >
                        <FileDown className="w-4 h-4 mr-2" />
                        Template {item.label}
                      </Button>
                    );
                  })}
                </div>
              </div>

              {/* File Upload */}
              <div>
                <Label className="text-sm font-medium mb-2 block">2. Selecteaza Fisier</Label>
                <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center hover:border-[#0052CC] transition-colors">
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={(e) => setSelectedFile(e.target.files[0])}
                    className="hidden"
                    id="import-file"
                  />
                  <label htmlFor="import-file" className="cursor-pointer">
                    <FileSpreadsheet className="w-10 h-10 mx-auto text-slate-400 mb-2" />
                    <p className="text-slate-600">
                      {selectedFile ? selectedFile.name : 'Click pentru a selecta fisier Excel'}
                    </p>
                    <p className="text-xs text-slate-400 mt-1">Formate acceptate: .xlsx, .xls</p>
                  </label>
                </div>
              </div>

              {/* Import Buttons */}
              <div>
                <Label className="text-sm font-medium mb-2 block">3. Importa Date</Label>
                <div className="flex flex-wrap gap-2">
                  {importItems.map((item) => {
                    const Icon = item.icon;
                    if (item.adminOnly && !hasRole('admin')) return null;
                    return (
                      <Button
                        key={item.type}
                        onClick={() => handleImport(item.type)}
                        disabled={!selectedFile || uploading}
                        data-testid={`import-${item.type}-btn`}
                      >
                        <Icon className="w-4 h-4 mr-2" />
                        Import {item.label}
                      </Button>
                    );
                  })}
                </div>
              </div>

              {/* Import Result */}
              {importResult && (
                <Alert className={importResult.success ? 'border-green-500 bg-green-50' : 'border-red-500 bg-red-50'}>
                  <div className="flex items-start gap-2">
                    {importResult.success ? (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    ) : (
                      <AlertCircle className="w-5 h-5 text-red-600" />
                    )}
                    <div>
                      <AlertDescription className={importResult.success ? 'text-green-700' : 'text-red-700'}>
                        {importResult.message}
                      </AlertDescription>
                      {importResult.errors && importResult.errors.length > 0 && (
                        <div className="mt-2 text-sm">
                          <p className="font-medium">Erori:</p>
                          <ul className="list-disc list-inside">
                            {importResult.errors.map((err, i) => (
                              <li key={i} className="text-red-600">{err}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                </Alert>
              )}
            </CardContent>
          </Card>

          {/* Instructions */}
          <Card className="card-industrial">
            <CardHeader>
              <CardTitle className="text-lg">Instructiuni Import</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm text-slate-600">
              <p>1. <strong>Descarca template-ul</strong> pentru tipul de date pe care doresti sa-l importi</p>
              <p>2. <strong>Completeaza datele</strong> in fisierul Excel, respectand formatul din template</p>
              <p>3. <strong>Campurile obligatorii</strong> sunt marcate cu * in header</p>
              <p>4. <strong>Selecteaza fisierul</strong> completat si apasa butonul de import</p>
              <p>5. Datele existente (dupa email/cod) vor fi <strong>actualizate</strong>, cele noi vor fi <strong>create</strong></p>
              <Alert>
                <AlertCircle className="w-4 h-4" />
                <AlertDescription>
                  Pentru utilizatorii noi importati, parola implicita este <strong>changeme123</strong>
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ImportExportPage;
